import { screeningApi } from "src/api/screening";
import { dashboardApi } from "src/api/dashboard";
import { UNAVAILABLE_SELECTION_NAME } from "src/utils/selection-label";

/** 월간 날짜 포맷 — "5월" */
const formatMonthDate = (dateValue) => {
  if (!dateValue) return null;
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return null;
  return `${d.getMonth() + 1}월`;
};

/** screening 응답에서 results 배열 추출 */
const extractScreening = (res) => {
  if (Array.isArray(res)) return res;
  if (res?.results && Array.isArray(res.results)) return res.results;
  return [];
};

/**
 * 그룹의 selectedSidos/Gungus/Dongs 배열을 RegionSummaryCard용 region 객체 배열로 변환.
 * - 시도/군구는 screening + SC/MP/RPC 통계 enrich
 * - 동은 SC/MP/RPC만 enrich (screening/RFD/DLG 미지원)
 *
 * regionIds 파라미터로 정확한 ID들만 fetch — topN 기반 호출은 동(3000+)에서 사용자 ID가
 * 응답에 포함되지 않아 이름/통계가 비는 문제가 있다.
 *
 * @param {object} group - { selectedSidos, selectedGungus, selectedDongs }
 * @param {object} [opts]
 * @param {object} [opts.sidos] - region.sidos store (이름/부모/인구수 fallback용)
 * @returns {Promise<Array>} RegionChartCard에 그대로 넘길 수 있는 region 배열
 */
export const enrichRegions = async (group, opts = {}) => {
  if (!group) return [];
  const { sidos } = opts;
  const sidoIds = group.selectedSidos || [];
  const gunguIds = group.selectedGungus || [];
  const dongIds = group.selectedDongs || [];
  const promises = [];

  if (sidoIds.length > 0) {
    const regionIds = sidoIds.join(",");
    promises.push(
      Promise.all([
        screeningApi.getResults({ regionType: "SIDO" }).catch(() => ({})),
        dashboardApi.getStats({ metrics: "SC_DEAL", regionType: "SIDO", regionIds }).catch(() => ({})),
        dashboardApi.getStats({ metrics: "MP_DEAL", regionType: "SIDO", regionIds }).catch(() => ({})),
        dashboardApi.getStats({ metrics: "RPC_DEAL_M", regionType: "SIDO", regionIds }).catch(() => ({})),
      ]).then(([scr, sc, mp, rpc]) => ({ level: "시도", screening: scr, sc, mp, rpc, ids: sidoIds })),
    );
  }
  if (gunguIds.length > 0) {
    const regionIds = gunguIds.join(",");
    promises.push(
      Promise.all([
        screeningApi.getResults({ regionType: "GUNGU" }).catch(() => ({})),
        dashboardApi.getStats({ metrics: "SC_DEAL", regionType: "GUNGU", regionIds }).catch(() => ({})),
        dashboardApi.getStats({ metrics: "MP_DEAL", regionType: "GUNGU", regionIds }).catch(() => ({})),
        dashboardApi.getStats({ metrics: "RPC_DEAL_M", regionType: "GUNGU", regionIds }).catch(() => ({})),
      ]).then(([scr, sc, mp, rpc]) => ({ level: "군구", screening: scr, sc, mp, rpc, ids: gunguIds })),
    );
  }
  if (dongIds.length > 0) {
    const regionIds = dongIds.join(",");
    promises.push(
      Promise.all([
        dashboardApi.getStats({ metrics: "SC_DEAL", regionType: "DONG", regionIds }).catch(() => ({})),
        dashboardApi.getStats({ metrics: "MP_DEAL", regionType: "DONG", regionIds }).catch(() => ({})),
        dashboardApi.getStats({ metrics: "RPC_DEAL_M", regionType: "DONG", regionIds }).catch(() => ({})),
      ]).then(([sc, mp, rpc]) => ({ level: "동", screening: {}, sc, mp, rpc, ids: dongIds })),
    );
  }

  const results = await Promise.all(promises);
  const regions = [];

  results.forEach(({ level, screening, sc, mp, rpc, ids }) => {
    const screeningArr = extractScreening(screening);
    const scStats = sc["SC_DEAL"] || [];
    const mpStats = mp["MP_DEAL"] || [];
    const rpcStats = rpc["RPC_DEAL_M"] || [];

    const screeningMap = {};
    screeningArr.forEach((s) => {
      const id = s.regionId || s.gunguId;
      if (id) screeningMap[id] = s;
    });

    const byId = (arr) => {
      const m = {};
      arr.forEach((s) => { if (s.regionId) m[s.regionId] = s; });
      return m;
    };
    const scMap = byId(scStats);
    const mpMap = byId(mpStats);
    const rpcMap = byId(rpcStats);

    ids.forEach((id) => {
      const scItem = scMap[id];
      const mpItem = mpMap[id];
      const rpcItem = rpcMap[id];
      const scrn = screeningMap[id];

      // sidos store에서 이름/부모/인구수 fallback (응답에 regionName 누락 시 ID 노출 방지)
      let storeName = null;
      let parentSidoName = null;
      let parentGunguName = null;
      let population = null;
      if (sidos) {
        if (level === "시도") {
          const s = sidos.getSido?.(id);
          storeName = s?.sidoName ?? null;
          population = s?.population ?? null;
        } else if (level === "군구") {
          const g = sidos.getGungu?.(id);
          storeName = g?.gunguName ?? null;
          parentSidoName = g?.sido?.sidoName ?? null;
          population = g?.population ?? null;
        } else if (level === "동") {
          const d = sidos.getDong?.(id);
          storeName = d?.dongName ?? null;
          parentSidoName = d?.sido?.sidoName ?? null;
          parentGunguName = d?.gungu?.gunguName ?? null;
        }
      }
      const name = scItem?.regionName || mpItem?.regionName || rpcItem?.regionName
        || scrn?.regionName || scrn?.gunguName || storeName || UNAVAILABLE_SELECTION_NAME;

      regions.push({
        id,
        name,
        levelLabel: level,
        parentSidoName,
        parentGunguName,
        population,
        screeningScore: scrn?.totalScore ?? null,
        screeningDate: scrn?.baseDate ? formatMonthDate(scrn.baseDate) : null,
        scStat: scItem || null,
        scChangeRate: scItem?.changeRate ?? null,
        mpStat: mpItem || null,
        mpChangeRate: mpItem?.changeRate ?? null,
        rpcStat: rpcItem || null,
        rpcChangeValue: rpcItem?.changeValue != null ? Math.round(rpcItem.changeValue) : null,
      });
    });
  });

  return regions;
};
